@file:OptIn(ExperimentalWasmJsInterop::class, ExperimentalWasmJsInterop::class)

package co.diena.everything.app.worker

import co.diena.everything.api.Bucket
import co.diena.everything.api.EntityId
import co.diena.everything.api.EverythingEvent
import co.diena.everything.api.ResolvedCategory
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.lortex.lines.common.LongInstant
import kotlin.js.ExperimentalWasmJsInterop
import kotlin.js.JsAny
import kotlin.js.JsArray
import kotlin.js.js

external interface WorkerApi : JsAny {
    fun sendMessage(
        message: String,
    )
}


fun createWorker(
    onMessage: (String) -> Unit,
): WorkerApi = js("appWorker.co.diena.everything.app.worker.createWorkerImpl(onMessage)")


sealed class WorkerCommand {
    data class LoadEvents(val buckets: List<Bucket>) : WorkerCommand()
    data class SetCategory(val category: EntityId?) : WorkerCommand()
    data class SubscribeToNewsLetter(val email: String) : WorkerCommand()
    data class AnalyticsEventClicked(val entity: EntityId) : WorkerCommand()
    data class Initialize(
        val language: String,
        val limitPerBucket: Int,
    ) : WorkerCommand()

    data class VisibilityChanged(val visible: Boolean) : WorkerCommand()


    fun encode(): String {
        return when (this) {
            is LoadEvents -> "L${encodeBuckets(this.buckets)}"
            is SetCategory -> "C${this.category?.toString() ?: ""}"
            is SubscribeToNewsLetter -> "S${this.email}"
            is AnalyticsEventClicked -> "A${this.entity}"
            is Initialize -> "I${this.language}:${this.limitPerBucket}"
            is VisibilityChanged -> "V${if (this.visible) "1" else "0"}"
        }
    }

    companion object {
        private fun encodeBuckets(buckets: List<Bucket>): String {
            return buckets.joinToString(",") { "${it.level}:${it.index}" }
        }

        private fun decodeBuckets(buckets: String): List<Bucket> {
            return buckets.split(",").map { it.split(":") }
                .map { Bucket(it[0].toInt(), it[1].toLong()) }
        }

        fun decode(message: String): WorkerCommand {
            return when (message.firstOrNull()) {
                'L' -> LoadEvents(decodeBuckets(message.drop(1)))
                'C' -> SetCategory(message.drop(1).takeIf { it.isNotEmpty() }
                    ?.let { EntityId.parse(it) })

                'S' -> SubscribeToNewsLetter(message.drop(1))
                'A' -> AnalyticsEventClicked(EntityId.parse(message.drop(1)))
                'I' -> {
                    val parts = message.drop(1).split(":")
                    if (parts.size != 2) {
                        throw IllegalArgumentException("Invalid Initialize command format: $message")
                    }
                    val language = parts[0]
                    val limitPerBucket = parts[1].toIntOrNull()
                        ?: throw IllegalArgumentException("Invalid limitPerBucket: ${parts[1]}")
                    Initialize(language, limitPerBucket)
                }

                'V' -> {
                    val visible = when (message.drop(1)) {
                        "1" -> true
                        "0" -> false
                        else -> throw IllegalArgumentException(
                            "Invalid visibility value: ${
                                message.drop(
                                    1
                                )
                            }"
                        )
                    }
                    VisibilityChanged(visible)
                }

                else -> throw IllegalArgumentException("Unknown command type: $message")
            }
        }
    }
}

sealed class WorkerMessage {
    @Serializable
    private data class EventsLoadedInternal(
        val addEvents: Map<String, EverythingEvent>,
        val removeEvents: Set<String>,
    )

    data class EventsLoaded(
        val addEvents: Map<EntityId, EverythingEvent>,
        val removeEvents: Set<EntityId>,
    ) : WorkerMessage()

    data class Error(val setMessage: LoaderError?) : WorkerMessage()
    data class NetworkReady(val ready: Boolean) : WorkerMessage()
    data class Loading(val pending: Boolean) : WorkerMessage()
    data class CategoriesLoaded(
        val categories: List<ResolvedCategory>,
    ) : WorkerMessage()

    data class NewsletterSubscribed(
        val success: Boolean,
    ) : WorkerMessage()


    fun encode(): String {
        return when (this) {
            is EventsLoaded -> {
                val data = EventsLoadedInternal(
                    addEvents = this.addEvents.mapKeys { it.key.toString() },
                    removeEvents = this.removeEvents.map { it.toString() }.toSet()
                )
                val json = Json.encodeToString(data)
                "E$json"
            }

            is Error -> {
                if (this.setMessage == null) {
                    "S"
                } else {
                    val errorJson = Json.encodeToString(this.setMessage)
                    "R${errorJson}"
                }
            }

            is Loading -> {
                if (this.pending) {
                    "L"
                } else {
                    "N"
                }
            }

            is CategoriesLoaded -> {
                val json = Json.encodeToString(this.categories)
                "G$json"
            }

            is NewsletterSubscribed -> {
                val json = Json.encodeToString(this.success)
                "W$json"
            }

            is NetworkReady -> {
                if (this.ready) {
                    "XY"
                } else {
                    "XN"
                }
            }
        }
    }

    companion object {
        fun decode(message: String): WorkerMessage {
            return when (message.firstOrNull()) {
                'E' -> {
                    val json = message.drop(1)
                    val data = Json.decodeFromString<EventsLoadedInternal>(json)
                    EventsLoaded(
                        addEvents = data.addEvents.mapKeys { EntityId.parse(it.key) },
                        removeEvents = data.removeEvents.map { EntityId.parse(it) }.toSet()
                    )
                }

                'S' -> Error(null)
                'R' -> {
                    val error = Json.decodeFromString<LoaderError>(message.drop(1))
                    Error(error)
                }

                'L' -> Loading(true)
                'N' -> Loading(false)
                'G' -> {
                    val categories = Json.decodeFromString<List<ResolvedCategory>>(message.drop(1))
                    CategoriesLoaded(categories)
                }

                'W' -> {
                    val success = Json.decodeFromString<Boolean>(message.drop(1))
                    NewsletterSubscribed(success)
                }

                'X' -> {
                    when (message.getOrNull(1)) {
                        'Y' -> NetworkReady(true)
                        'N' -> NetworkReady(false)
                        else -> throw IllegalArgumentException("Invalid network ready message: $message")
                    }
                }

                else -> throw IllegalArgumentException("Unknown message type: $message")
            }
        }
    }
}